import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const services = [
  "Kontorsstädning",
  "Byggstädning",
  "Butiksstädning",
  "Städning av arbetsbodar",
  "Golvvård",
  "Annat",
];

// Central Elido-formmotor på Cloudflare Worker.
const FORMS_URL = "https://forms.elido.nu/api/contact";
const SITE_ID = "rena-tjanster";

function clientValidate(form) {
  const hasIdentity = form.name.trim() || form.company.trim();
  const hasContact = form.email.trim() || form.phone.trim();
  const hasIntent = form.message.trim() || form.service.trim();
  if (!hasIdentity) return "Fyll i namn eller företag.";
  if (!hasContact) return "Fyll i e-post eller telefon så vi kan återkomma.";
  if (!hasIntent) return "Berätta kort vad ärendet gäller eller välj en tjänst.";
  return null;
}

export default function QuoteForm({ defaultService = "" }) {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [submissionId, setSubmissionId] = useState(() => crypto.randomUUID());
  const [form, setForm] = useState({
    company: "",
    name: "",
    email: "",
    phone: "",
    service: defaultService,
    message: "",
    website: "", // honeypot – ska förbli tom
  });

  const handle = (k) => (e) => {
    setForm({ ...form, [k]: e?.target?.value ?? e });
    setSubmissionId(crypto.randomUUID());
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;

    const validationError = clientValidate(form);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setSending(true);
    try {
      const company = form.company.trim();
      const name = form.name.trim();
      const service = form.service.trim();
      const freeText = form.message.trim();

      const res = await fetch(FORMS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          site_id: SITE_ID,
          submission_id: submissionId,
          website: form.website,
          subject: service ? `Offertförfrågan – ${service}` : "Offertförfrågan",
          name: name || company,
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          company,
          service,
          description: freeText || "(Inget meddelande angivet)",
          message: freeText || service || "Offertförfrågan",
          ...(window.ElidoAttribution?.getPayload?.() || {}),
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok || !data?.emailed) {
        toast.error("Något gick fel när förfrågan skulle skickas. Försök igen eller kontakta oss direkt via e-post.");
        return;
      }

      toast.success("Tack för din förfrågan! Vi har tagit emot den och återkommer så snart vi kan.", { duration: 6000 });
      setSubmitted(true);
      setForm({ company: "", name: "", email: "", phone: "", service: defaultService, message: "", website: "" });
      setSubmissionId(crypto.randomUUID());
    } catch (err) {
      toast.error("Något gick fel när förfrågan skulle skickas. Försök igen eller kontakta oss direkt via e-post.");
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <div
        id="offertformular"
        className="bg-white border border-slate-100 rounded-2xl p-10 text-center shadow-sm scroll-mt-32"
        data-testid="quote-form-success"
      >
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#F59E0B]/15 text-[#1E3A6F] mb-4">
          <CheckCircle2 className="h-7 w-7" />
        </div>
        <h2 className="font-display text-2xl font-semibold mb-2">Tack för din förfrågan!</h2>
        <p className="text-slate-600">
          Vi har tagit emot den och återkommer så snart vi kan.
        </p>
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          className="mt-6 text-sm text-[#1E3A6F] hover:text-[#152a51] underline underline-offset-2"
          data-testid="quote-form-new"
        >
          Skicka en ny förfrågan
        </button>
      </div>
    );
  }

  return (
    <form
      id="offertformular"
      onSubmit={onSubmit}
      className="bg-white border border-slate-100 rounded-2xl shadow-sm p-8 lg:p-10 scroll-mt-32"
      data-testid="quote-form"
      aria-label="Offertförfrågan"
      noValidate
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Honeypot – dolt fält för spambots */}
        <div aria-hidden="true" className="hidden" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
          <label htmlFor="qf-website">Webbplats (lämna tom)</label>
          <input
            id="qf-website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={handle("website")}
          />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="qf-company" className="text-sm font-medium text-slate-700">Företag / verksamhet</Label>
          <Input
            id="qf-company"
            value={form.company}
            onChange={handle("company")}
            placeholder="Företagsnamn eller verksamhet"
            className="mt-1.5 h-12 rounded-lg border-slate-200"
            data-testid="qf-company"
          />
        </div>
        <div>
          <Label htmlFor="qf-name" className="text-sm font-medium text-slate-700">Kontaktperson</Label>
          <Input
            id="qf-name"
            value={form.name}
            onChange={handle("name")}
            placeholder="För- och efternamn"
            className="mt-1.5 h-12 rounded-lg border-slate-200"
            data-testid="qf-name"
          />
        </div>
        <div>
          <Label htmlFor="qf-email" className="text-sm font-medium text-slate-700">E-post</Label>
          <Input
            id="qf-email"
            type="email"
            value={form.email}
            onChange={handle("email")}
            placeholder="namn@foretag.se"
            className="mt-1.5 h-12 rounded-lg border-slate-200"
            data-testid="qf-email"
          />
        </div>
        <div>
          <Label htmlFor="qf-phone" className="text-sm font-medium text-slate-700">Telefon</Label>
          <Input
            id="qf-phone"
            value={form.phone}
            onChange={handle("phone")}
            placeholder="070-000 00 00"
            className="mt-1.5 h-12 rounded-lg border-slate-200"
            data-testid="qf-phone"
          />
        </div>
        <div>
          <Label className="text-sm font-medium text-slate-700">Typ av tjänst</Label>
          <Select value={form.service} onValueChange={handle("service")}>
            <SelectTrigger className="mt-1.5 h-12 rounded-lg border-slate-200" data-testid="qf-service">
              <SelectValue placeholder="Välj tjänst..." />
            </SelectTrigger>
            <SelectContent>
              {services.map((s) => (
                <SelectItem key={s} value={s} data-testid={`qf-service-opt-${s}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="md:col-span-2">
          <Label htmlFor="qf-message" className="text-sm font-medium text-slate-700">Meddelande</Label>
          <Textarea
            id="qf-message"
            rows={4}
            value={form.message}
            onChange={handle("message")}
            placeholder="Berätta kort om ert behov – lokal, ytor, frekvens och önskad starttid."
            className="mt-1.5 rounded-lg border-slate-200"
            data-testid="qf-message"
          />
        </div>
      </div>

      <Button
        type="submit"
        disabled={sending}
        className="mt-6 w-full md:w-auto bg-[#1E3A6F] hover:bg-[#152a51] text-white rounded-full px-8 py-6 font-medium transition-all hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        data-testid="qf-submit"
      >
        {sending ? (
          <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Skickar...</>
        ) : (
          <><Send className="mr-2 h-4 w-4" /> Skicka offertförfrågan</>
        )}
      </Button>
      <p className="mt-4 text-xs text-slate-500 leading-relaxed">
        Genom att skicka samtycker ni till att uppgifterna används för att hantera er förfrågan.{" "}
        <a href="/integritetspolicy" className="underline hover:text-[#1E3A6F]">Läs vår integritetspolicy</a>.
      </p>
    </form>
  );
}
